import { resolveRouteParam } from "@-/com.npmjs.expo-router";
import runes from "@-/com.npmjs.runes2";
import { usePrefetchedSuspenseQueries } from "@-/com.npmjs.tanstack__react-query";
import type {
  GraphemeMorphemeMnemonicsPanelMorphemeSlice,
  GraphemeMorphemeMnemonicsPanelMorphemeUserSlice,
} from "@-/dev.improve.haniverse.lib.morpheme.entities";
import {
  useMorphemeMnemonicsPanelMorphemeSliceQueryOptions,
  useMorphemeMnemonicsPanelMorphemeUserSliceQueryOptions,
} from "@-/dev.improve.haniverse.lib.morpheme.read";
import { useResolvedQuery } from "@-/dev.improve.haniverse.lib.query-options";
import { MorphemeNotFound } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.not-found";
import { MorphemePanelsContextProvider } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import { GraphemeMorphemePanelsView } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme";
import { GraphemePreviewSheetProvider } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.preview-sheet";
import { WordMorphemePanelsView } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.word";
import type { MorphemeHrefObject } from "@-/dev.improve.haniverse.lib.morpheme.href.entities";
import type { DataTag, QueryKey } from "@-/com.npmjs.tanstack__react-query";
import type {
  MorphemePanelsActivePanel,
  MorphemePanelsMnemonicsNavigationProps,
} from "#morpheme-panels";
import { type ReactNode } from "react";
import { hasMinLength } from "@-/com.npmjs.ts-array-length";

/** The grapheme two-panel view, below the gate that narrowed its readonly slice. */
function MorphemePanelsGraphemeView({
  activePanel,
  headerExpansionContent,
  onBack,
  readonlySlice,
  readonlySliceQueryKey,
  userSlice,
  userSliceQueryKey,
}: {
  activePanel: MorphemePanelsActivePanel;
  headerExpansionContent?: ReactNode;
  onBack?: () => void;
  readonlySlice: GraphemeMorphemeMnemonicsPanelMorphemeSlice;
  readonlySliceQueryKey: DataTag<
    QueryKey,
    GraphemeMorphemeMnemonicsPanelMorphemeSlice | null,
    Error
  >;
  userSlice: GraphemeMorphemeMnemonicsPanelMorphemeUserSlice;
  userSliceQueryKey: DataTag<QueryKey, GraphemeMorphemeMnemonicsPanelMorphemeUserSlice, Error>;
}) {
  const resolvedReadonlySlice = useResolvedQuery(readonlySliceQueryKey, readonlySlice);
  const resolvedUserSlice = useResolvedQuery(userSliceQueryKey, userSlice);

  return (
    <GraphemeMorphemePanelsView
      activePanel={activePanel}
      headerExpansionContent={headerExpansionContent}
      onBack={onBack}
      readonlySlice={resolvedReadonlySlice}
      userSlice={resolvedUserSlice}
    />
  );
}

/**
 * The morpheme panels as ONE component (the Learn review card's exploration): the gate for the
 * morpheme's mnemonics slices — the only observer of them in this subtree — then either the word
 * info view, `MorphemeNotFound`, or the grapheme two-panel view.
 */
export function MorphemePanelsContent({
  activeIndex,
  activePanel,
  headerExpansionContent,
  morpheme,
  morphemeHrefObject,
  onBack,
  setActiveIndex,
  viewNavigationRef,
}: {
  activePanel: MorphemePanelsActivePanel;
  headerExpansionContent?: ReactNode;
  morpheme: string | string[];
  morphemeHrefObject: MorphemeHrefObject;
  onBack?: () => void;
} & MorphemePanelsMnemonicsNavigationProps) {
  const character = resolveRouteParam(morpheme);
  const mnemonicsPanelMorphemeSliceQueryOptions =
    useMorphemeMnemonicsPanelMorphemeSliceQueryOptions(character);
  const mnemonicsPanelMorphemeUserSliceQueryOptions =
    useMorphemeMnemonicsPanelMorphemeUserSliceQueryOptions(character);
  const [{ data: mnemonicsPanelMorphemeSlice }, { data: mnemonicsPanelMorphemeUserSlice }] =
    usePrefetchedSuspenseQueries({
      queries: [
        mnemonicsPanelMorphemeSliceQueryOptions,
        mnemonicsPanelMorphemeUserSliceQueryOptions,
      ],
    });

  // Words only have a info view, so they bypass the grapheme meaning-card flow (which
  // powers the mnemonics panel and only ever resolves single-grapheme cards).
  //
  // Detect the word by CODEPOINT COUNT, not by an info slice's `morphemeType`: this host fetches no
  // info slice — each info screen self-fetches (and gates) its own. Same test, and same reasoning,
  // as `MorphemePanelsLayout` on the card routes.
  if (activePanel === "info" && hasMinLength(runes(character), 2)) {
    return <WordMorphemePanelsView word={character} />;
  }

  if (mnemonicsPanelMorphemeSlice === null) {
    return <MorphemeNotFound morpheme={character} />;
  }

  return (
    <MorphemePanelsContextProvider
      activeIndex={activeIndex}
      character={character}
      morphemeHrefObject={morphemeHrefObject}
      setActiveIndex={setActiveIndex}
      viewNavigationRef={viewNavigationRef}
    >
      {/* Mnemonic links and decomposition components inside this subtree preview the linked
          grapheme in a sheet instead of navigating, so a reader can check a component without
          losing the character they were studying. */}
      <GraphemePreviewSheetProvider morphemeHrefObject={morphemeHrefObject}>
        <MorphemePanelsGraphemeView
          activePanel={activePanel}
          headerExpansionContent={headerExpansionContent}
          onBack={onBack}
          readonlySlice={mnemonicsPanelMorphemeSlice}
          readonlySliceQueryKey={mnemonicsPanelMorphemeSliceQueryOptions.queryKey}
          userSlice={mnemonicsPanelMorphemeUserSlice}
          userSliceQueryKey={mnemonicsPanelMorphemeUserSliceQueryOptions.queryKey}
        />
      </GraphemePreviewSheetProvider>
    </MorphemePanelsContextProvider>
  );
}
